'use client';

import { devToolsEnabled } from '@/lib/dev-tools';
import { apiFetch } from '@/lib/utils/api';
import { Button, Grid, Row, Spinner, Stack, Text } from '@slims/ui/atoms';
import {
	AttachmentPill,
	CardBadge,
	CardGrid,
	ConfirmationDialog,
	DetailField,
	DetailSelectInput,
	DetailTextarea,
	DetailTextInput,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	PanelCard,
	QuoteBlock,
	StatusBlock,
	UploadAttachButton,
} from '@slims/ui/molecules';
import {
	AdminNotificationRow,
	AttachmentViewerDialog,
	NotificationEmpty,
	NotificationRowList,
} from '@slims/ui/organisms';
import { PageHeader } from '@slims/ui/templates';
import {
	AlertCircle,
	ArrowRight,
	CheckCheck,
	FlaskConical,
	Info,
	Mail,
	SquarePen,
	Trash2,
	Wallet,
} from 'lucide-react';
import { useRouter, useSearchParams } from '@/lib/next/navigation';
import { Suspense, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useMembers } from '@/hooks/use-members';
import { useContextOrg } from '@/hooks/use-context-org';
import type { DBNotification } from '@/hooks/use-notifications';
import { TEST_NOTIFICATION_CANALE, useNotifications } from '@/hooks/use-notifications';
import { useSystemAlerts } from '@/hooks/use-system-alerts';
import { useAuthStore } from '@/stores/auth-store';

const TIPO_CONFIG = {
	expiry_alert: { icon: AlertCircle, emoji: '⏰' },
	missing_documents: { icon: Info, emoji: '📋' },
	pod_anomaly: { icon: AlertCircle, emoji: '⚠️' },
	monthly_report: { icon: Mail, emoji: '📊' },
	weekly_summary: { icon: CheckCheck, emoji: '✓' },
	manual: { icon: ArrowRight, emoji: '📧' },
	system: { icon: Info, emoji: '🏛️' },
	deadline_acconto: { icon: AlertCircle, emoji: '⏰' },
	deadline_conguaglio: { icon: AlertCircle, emoji: '⏰' },
	deadline_rendicontazione: { icon: AlertCircle, emoji: '⏰' },
	deadline_pnrr: { icon: AlertCircle, emoji: '⏰' },
	deadline_pnrr_saldo: { icon: AlertCircle, emoji: '⏰' },
	deadline_domanda_gse: { icon: AlertCircle, emoji: '⏰' },
	deadline_integration_scaduta: { icon: AlertCircle, emoji: '⏰' },
	deadline_antimafia: { icon: AlertCircle, emoji: '🛡️' },
	antimafia_threshold: { icon: AlertCircle, emoji: '🛡️' },
	live_feed_down: { icon: AlertCircle, emoji: '📡' },
	forecast_accuracy_low: { icon: AlertCircle, emoji: '🎯' },
	consumption_anomaly: { icon: AlertCircle, emoji: '📈' },
	wallet_soglia: { icon: Wallet, emoji: '💰' },
};

// Etichette di visualizzazione per l'intero enum, inclusi i tipi che solo il
// cron (o il generatore di notifiche di test) produce.
const TIPO_LABEL: Record<DBNotification['tipo'], string> = {
	manual: 'Manuale',
	expiry_alert: 'Alert Scadenza',
	missing_documents: 'Documenti Mancanti',
	pod_anomaly: 'Anomalia POD',
	monthly_report: 'Report Mensile',
	weekly_summary: 'Riepilogo Settimanale',
	system: 'Sistema',
	deadline_acconto: 'Scadenza Acconto',
	deadline_conguaglio: 'Scadenza Conguaglio',
	deadline_rendicontazione: 'Scadenza Rendicontazione',
	deadline_pnrr: 'Scadenza PNRR',
	deadline_pnrr_saldo: 'Saldo PNRR',
	deadline_domanda_gse: 'Domanda GSE',
	deadline_integration_scaduta: 'Integrazione Scaduta',
	deadline_antimafia: 'Scadenza Antimafia',
	antimafia_threshold: 'Soglia Antimafia',
	live_feed_down: 'Feed Interrotto',
	forecast_accuracy_low: 'Accuratezza Previsioni',
	consumption_anomaly: 'Irregolarità Consumi',
	wallet_soglia: 'Wallet - Soglia Superata',
};

// Sottoinsieme selezionabile nella compose manuale.
const TIPO_OPTIONS: { value: DBNotification['tipo']; label: string }[] = (
	['manual', 'pod_anomaly', 'monthly_report', 'weekly_summary', 'system'] as const
).map((value) => ({ value, label: TIPO_LABEL[value] }));

/**
 * Valore sentinella del menu "Destinatario": sul database il broadcast è
 * `member_id` nullo, ma nel form serve distinguerlo dal "non ho ancora scelto".
 * Prima l'unico modo di indirizzare un socio era *non* lasciare vuoto un campo
 * marcato opzionale, e ogni comunicazione partiva a tutti (INB-111).
 */
const ALL_MEMBERS = '__all__';

type Allegato = { path: string; nome: string };

function parseAllegati(path: string | null, nome: string | null): Allegato[] {
	if (!path) return [];
	try {
		const paths = JSON.parse(path) as string[];
		const nomi = JSON.parse(nome ?? '[]') as string[];
		return paths.map((p, i) => ({ path: p, nome: nomi[i] ?? p.split('/').pop() ?? 'Allegato' }));
	} catch {
		return [{ path, nome: nome ?? path.split('/').pop() ?? 'Allegato' }];
	}
}

function formatNotifDate(raw: string | null | undefined): string {
	if (!raw) return '-';
	const d = new Date(raw);
	const now = new Date();
	const isToday = d.toDateString() === now.toDateString();
	const dateStr = isToday
		? 'Oggi'
		: d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
	const timeStr = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
	return `${dateStr} · ${timeStr}`;
}

function getInboundEmoji(notif: DBNotification): string {
	const t = notif.titolo;
	if (/subentro|pod.?takeover|takeover/i.test(t)) return '🔄';
	if (/recesso|withdrawal|ritiro/i.test(t)) return '🚪';
	if (/document|allegat/i.test(t)) return '📄';
	if (/scadenza|alert|mancant|incomplet|integraz/i.test(t)) return '⏰';
	return (TIPO_CONFIG[notif.tipo] ?? TIPO_CONFIG.system).emoji;
}

function isPinnedNotif(n: { tipo: string; canale: string | null; titolo: string }): boolean {
	if (n.tipo === 'expiry_alert') return true;
	return /\bgse\b|scadenza|conguaglio/i.test(n.titolo);
}

// Notifiche automatiche/di sistema (GSE, scadenze, anomalie POD, report),
// distinte dai messaggi bidirezionali Socio↔Amministratore (tipo 'manual').
const SISTEMA_TIPI = new Set<DBNotification['tipo']>([
	'expiry_alert',
	'missing_documents',
	'pod_anomaly',
	'monthly_report',
	'weekly_summary',
	'system',
	'deadline_acconto',
	'deadline_conguaglio',
	'deadline_rendicontazione',
	'deadline_pnrr',
	'deadline_pnrr_saldo',
	'deadline_domanda_gse',
	'deadline_integration_scaduta',
	'deadline_antimafia',
	'antimafia_threshold',
	'live_feed_down',
	'forecast_accuracy_low',
	'consumption_anomaly',
]);
function isSystemNotif(n: DBNotification): boolean {
	return SISTEMA_TIPI.has(n.tipo) || isPinnedNotif(n);
}

function parseNotifExpiry(titolo: string): Date | null {
	const MONTHS: Record<string, number> = {
		gennaio: 0,
		febbraio: 1,
		marzo: 2,
		aprile: 3,
		maggio: 4,
		giugno: 5,
		luglio: 6,
		agosto: 7,
		settembre: 8,
		ottobre: 9,
		novembre: 10,
		dicembre: 11,
	};
	const m = titolo.match(
		/(\d{1,2})\s+(gennaio|febbraio|marzo|aprile|maggio|giugno|luglio|agosto|settembre|ottobre|novembre|dicembre)/i,
	);
	if (!m) return null;
	return new Date(new Date().getFullYear(), MONTHS[m[2].toLowerCase()], parseInt(m[1], 10));
}

const EMPTY_FORM = {
	titolo: '',
	messaggio: '',
	tipo: 'manual' as DBNotification['tipo'],
	direction: 'outbound' as DBNotification['direction'],
	memberId: '',
	canale: 'portale' as string,
	letto: false,
	allegati: [] as Allegato[],
};

function NotificationsContent() {
	const contextOrg = useContextOrg();
	const adminConfigId = contextOrg?.orgId ?? null;
	const {
		notifications,
		isLoading,
		error,
		markAsRead,
		createNotification,
		deleteNotification,
		generateTestNotifications,
		deleteTestNotifications,
	} = useNotifications({
		orgId: adminConfigId,
	});
	const { members } = useMembers(adminConfigId);
	// Avvisi calcolati dai dati (stato AI Prediction Engine, previsione della
	// settimana, report builder): stanno accanto alle notifiche di sistema
	// salvate, non in cima alle pagine che descrivono.
	const systemAlerts = useSystemAlerts(adminConfigId, 'admin');
	const searchParams = useSearchParams();
	const router = useRouter();
	const [newOpen, setNewOpen] = useState(false);
	const [form, setForm] = useState(EMPTY_FORM);

	function openNewNotif(direction: DBNotification['direction']) {
		setForm({ ...EMPTY_FORM, direction });
		setTitoloError('');
		setDestinatarioError('');
		setNewOpen(true);
	}
	const [titoloError, setTitoloError] = useState('');
	const [destinatarioError, setDestinatarioError] = useState('');
	const [saving, setSaving] = useState(false);
	const [uploadingAttachment, setUploadingAttachment] = useState(false);
	const [detailNotif, setDetailNotif] = useState<DBNotification | null>(null);
	const [viewAttachUrl, setViewAttachUrl] = useState<{ url: string; nome: string } | null>(null);
	const [pendingDelete, setPendingDelete] = useState<DBNotification | null>(null);
	const [deleting, setDeleting] = useState(false);

	const [testBusy, setTestBusy] = useState(false);

	async function handleConfirmDelete() {
		if (!pendingDelete) return;
		setDeleting(true);
		try {
			await deleteNotification(pendingDelete.id);
			// La riga sparisce dalla lista: se era aperta nel dettaglio, chiudilo.
			setDetailNotif((current) => (current?.id === pendingDelete.id ? null : current));
			toast.success('Notifica eliminata');
			setPendingDelete(null);
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore eliminazione notifica');
		} finally {
			setDeleting(false);
		}
	}

	async function uploadAllegati(files: FileList | null): Promise<Allegato[]> {
		if (!files || files.length === 0) return [];
		const uploaded: Allegato[] = [];
		for (const file of Array.from(files)) {
			const safeName = file.name.replace(/\s+/g, '_');
			const path = `documents/notifiche/${Date.now()}_${safeName}`;
			const formData = new FormData();
			formData.append('file', file);
			formData.append('path', path);
			const res = await apiFetch(`/api/storage/upload?orgId=${adminConfigId}`, {
				method: 'POST',
				body: formData,
			});
			const json = await res.json();
			if (!res.ok) throw new Error(json.error);
			uploaded.push({ path: json.publicUrl, nome: file.name });
		}
		return uploaded;
	}

	const openNotifId = searchParams.get('notifica');
	useEffect(() => {
		if (!openNotifId || isLoading || notifications.length === 0) return;
		const target = notifications.find((n) => n.id === openNotifId);
		if (!target) return;
		setDetailNotif(target);
		if (!target.letto) markAsRead(target.id);
		router.replace('/notifications', { scroll: false });
	}, [openNotifId, isLoading, notifications, markAsRead, router]);

	async function handleAttachmentUpload(files: FileList | null) {
		setUploadingAttachment(true);
		try {
			const uploaded = await uploadAllegati(files);
			if (uploaded.length === 0) return;
			setForm((f) => ({ ...f, allegati: [...f.allegati, ...uploaded] }));
			toast.success(
				`${uploaded.length} allegato${uploaded.length > 1 ? 'i' : ''} caricato${uploaded.length > 1 ? 'i' : ''}`,
			);
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore caricamento allegato');
		} finally {
			setUploadingAttachment(false);
		}
	}

	// "Tutti i soci" resta possibile, ma come voce da scegliere: non è più ciò
	// che succede lasciando stare il campo.
	const destinatarioOptions = [
		{
			value: ALL_MEMBERS,
			label: `Tutti i soci${members.length > 0 ? ` (${members.length})` : ''}`,
		},
		...members.map((m) => ({ value: m.id, label: m.nome })),
	];

	function destinatarioLabel(memberId: string): string {
		if (memberId === ALL_MEMBERS) return 'tutti i soci';
		return members.find((m) => m.id === memberId)?.nome ?? 'tutti i soci';
	}

	async function handleCreate(e: React.SyntheticEvent<HTMLFormElement>) {
		e.preventDefault();
		const missingTitolo = !form.titolo.trim();
		// Il destinatario è obbligatorio solo in uscita: in entrata identifica il
		// socio *mittente* di un messaggio trascritto a mano, e può mancare.
		const missingDestinatario = form.direction === 'outbound' && !form.memberId;
		setTitoloError(missingTitolo ? 'Il titolo è obbligatorio' : '');
		setDestinatarioError(missingDestinatario ? 'Scegli chi deve ricevere la comunicazione' : '');
		if (missingTitolo || missingDestinatario) return;
		setSaving(true);
		try {
			const created = await createNotification({
				titolo: form.titolo.trim(),
				messaggio: form.messaggio || null,
				tipo: form.tipo,
				direction: form.direction,
				memberId: form.memberId && form.memberId !== ALL_MEMBERS ? form.memberId : null,
				canale: form.canale || null,
				letto: false,
				allegatoPath:
					form.allegati.length > 0 ? JSON.stringify(form.allegati.map((a) => a.path)) : null,
				allegatoNome:
					form.allegati.length > 0 ? JSON.stringify(form.allegati.map((a) => a.nome)) : null,
				recipientRole: form.direction === 'outbound' ? 'cer_user' : 'cer_admin',
				requestId: null,
			});

			setNewOpen(false);
			setForm(EMPTY_FORM);
			setTitoloError('');
			setDestinatarioError('');
			const delivery = created.delivery;
			const notDelivered = delivery ? delivery.email.skipped + delivery.email.failed : 0;
			if (notDelivered > 0) {
				toast.warning(
					`Comunicazione salvata. ${notDelivered} invii esterni non eseguiti per contatto, preferenza o configurazione mancante.`,
				);
			} else {
				// Il destinatario nel messaggio di conferma: un broadcast partito per
				// sbaglio si nota subito, non a valle dai reclami dei soci (INB-111).
				toast.success(`Comunicazione inviata a ${destinatarioLabel(form.memberId)}`);
			}
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore invio');
		} finally {
			setSaving(false);
		}
	}

	// Notifiche di test: generate su richiesta per popolare i pannelli senza
	// aspettare i cron (anomalie, report). Rigenerare sostituisce il
	// set precedente, così i click ripetuti non accumulano dati.
	const testCount = notifications.filter((n) => n.canale === TEST_NOTIFICATION_CANALE).length;

	async function handleGenerateTest() {
		if (!adminConfigId) {
			toast.error('Nessuna configurazione selezionata');
			return;
		}
		setTestBusy(true);
		try {
			const created = await generateTestNotifications({
				orgId: adminConfigId,
				memberId: members[0]?.id ?? null,
			});
			toast.success(`${created} notifiche di test generate`);
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore generazione notifiche di test');
		} finally {
			setTestBusy(false);
		}
	}

	async function handleDeleteTest() {
		if (!adminConfigId) return;
		setTestBusy(true);
		try {
			const deleted = await deleteTestNotifications(adminConfigId);
			toast.success(`${deleted} notifiche di test eliminate`);
		} catch (err: unknown) {
			toast.error(err instanceof Error ? err.message : 'Errore eliminazione notifiche di test');
		} finally {
			setTestBusy(false);
		}
	}

	// Sort inbound: 1=sistema/scadenze (red), 2=non lette (amber), 3=lette (cyan)
	const sortNotifs = (list: DBNotification[]) =>
		[...list]
			.filter((n) => {
				// Sistema notifications disappear after expiry unless read
				if (!isPinnedNotif(n)) return true;
				if (n.letto) return true;
				const expiry = parseNotifExpiry(n.titolo);
				return !expiry || new Date() < expiry;
			})
			.sort((a, b) => {
				const grp = (n: DBNotification) => {
					if (isPinnedNotif(n)) return 0;
					if (!n.letto) return 1;
					return 2;
				};
				const ga = grp(a);
				const gb = grp(b);
				if (ga !== gb) return ga - gb;
				if (ga === 0) {
					const ea = parseNotifExpiry(a.titolo);
					const eb = parseNotifExpiry(b.titolo);
					if (ea && eb) return ea.getTime() - eb.getTime();
					if (ea) return -1;
					if (eb) return 1;
				}
				return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
			});

	// Notifiche di sistema: alert automatici, separati dai messaggi bidirezionali.
	const systemSorted = sortNotifs(notifications.filter(isSystemNotif));

	// In ingresso: solo messaggi conversazionali Socio→Amministratore.
	const inboundSorted = sortNotifs(
		notifications.filter(
			(n) => n.direction === 'inbound' && n.recipientRole === 'cer_admin' && !isSystemNotif(n),
		),
	);

	const outboundSorted = notifications
		.filter((n) => n.direction === 'outbound')
		.sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());

	const renderInboundItem = (notif: DBNotification) => {
		const isPin = isPinnedNotif(notif);
		const memberName = notif.memberId
			? (members.find((m) => m.id === notif.memberId)?.nome ?? `#${notif.memberId.slice(0, 8)}`)
			: null;

		return (
			<AdminNotificationRow
				key={notif.id}
				tone={isPin ? 'pinned' : !notif.letto ? 'unread' : 'read'}
				emoji={getInboundEmoji(notif)}
				title={notif.titolo}
				message={
					notif.messaggio
						? notif.messaggio.length > 90
							? `${notif.messaggio.slice(0, 90)}...`
							: notif.messaggio
						: null
				}
				metaLabel={`${formatNotifDate(notif.createdAt)}${memberName ? ` · ${memberName}` : ''}${notif.canale ? ` · ${notif.canale}` : ''}`}
				onOpen={() => {
					setDetailNotif(notif);
					if (!notif.letto) markAsRead(notif.id);
				}}
				onDelete={() => setPendingDelete(notif)}
			/>
		);
	};

	return (
		<div>
			{/* ── Header ─────────────────────────────────────────────────────── */}
			<PageHeader
				title="Centro Notifiche"
				actions={
					<>
						{testCount > 0 && (
							<>
								<CardBadge tone="orange">{testCount} di test</CardBadge>
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={testBusy}
									onClick={handleDeleteTest}
								>
									<Trash2 />
									Elimina test
								</Button>
							</>
						)}
						{/* Strumento di prova: solo in sviluppo locale (INB-371). «Elimina test»
						    resta, per ripulire quelle generate prima. */}
						{devToolsEnabled() && (
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={testBusy || !adminConfigId}
								onClick={handleGenerateTest}
							>
								{testBusy ? <Spinner size="sm" tone="inherit" /> : <FlaskConical />}
								Genera notifiche di test
							</Button>
						)}
						{/* Lo stesso invio del pulsante in fondo a «Comunicazioni Inviate»,
						    raggiungibile senza scorrere la pagina. */}
						<Button
							type="button"
							variant="outline"
							size="icon"
							title="Nuova comunicazione"
							aria-label="Nuova comunicazione"
							onClick={() => openNewNotif('outbound')}
						>
							<SquarePen />
						</Button>
					</>
				}
			/>

			{/* ── Content ─────────────────────────────────────────────────────── */}
			{isLoading ? (
				<StatusBlock kind="loading" size="lg" />
			) : error ? (
				<StatusBlock kind="error" size="lg" message={error} />
			) : (
				<>
					<Stack mb="md">
						<PanelCard
							title="Notifiche di Sistema"
							aside={
								<CardBadge tone="orange">
									{systemSorted.filter((n) => !n.letto).length} non lette
								</CardBadge>
							}
						>
							{systemSorted.length === 0 && systemAlerts.length === 0 ? (
								<NotificationEmpty>Nessuna notifica di sistema.</NotificationEmpty>
							) : (
								<NotificationRowList>
									{systemAlerts.map((alert) => (
										<AdminNotificationRow
											key={alert.id}
											tone={alert.actionRequired ? 'pinned' : 'read'}
											emoji={alert.emoji}
											title={alert.title}
											message={alert.message}
											metaLabel={`Stato configurazione · ${alert.badge}`}
											onOpen={() => router.push(alert.href)}
										/>
									))}
									{systemSorted.map(renderInboundItem)}
								</NotificationRowList>
							)}
						</PanelCard>
					</Stack>
					<CardGrid columns={2}>
						{/* ── Left: Inbound ──────────────────────────────────────── */}
						<PanelCard
							title="Notifiche in Ingresso"
							aside={
								<CardBadge tone="orange">
									{
										notifications.filter(
											(n) =>
												n.direction === 'inbound' && n.recipientRole === 'cer_admin' && !n.letto,
										).length
									}{' '}
									non lette
								</CardBadge>
							}
						>
							{inboundSorted.length === 0 ? (
								<NotificationEmpty>Nessuna notifica in ingresso.</NotificationEmpty>
							) : (
								<NotificationRowList>{inboundSorted.map(renderInboundItem)}</NotificationRowList>
							)}
						</PanelCard>

						{/* ── Right: Outbound ────────────────────────────────────── */}
						<PanelCard
							title="Notifiche in Uscita"
							aside={<CardBadge tone="blue">Inviate</CardBadge>}
						>
							{outboundSorted.length === 0 ? (
								<NotificationEmpty>Nessuna notifica in uscita.</NotificationEmpty>
							) : (
								<NotificationRowList>
									{outboundSorted.map((notif) => {
										const cfg = TIPO_CONFIG[notif.tipo] ?? TIPO_CONFIG.system;
										const memberName = notif.memberId
											? (members.find((m) => m.id === notif.memberId)?.nome ??
												`#${notif.memberId.slice(0, 8)}`)
											: 'Tutti i soci';
										const data = formatNotifDate(notif.createdAt);
										return (
											<AdminNotificationRow
												key={notif.id}
												tone="sent"
												emoji={cfg.emoji}
												title={`${memberName} - ${notif.titolo}`}
												message={
													notif.messaggio
														? notif.messaggio.length > 80
															? `${notif.messaggio.slice(0, 80)}...`
															: notif.messaggio
														: null
												}
												metaLabel={`${data}${notif.canale ? ` · ${notif.canale}` : ''}${notif.letto ? ' · Aperta ✓' : ''}`}
												onOpen={() => setDetailNotif(notif)}
												onDelete={() => setPendingDelete(notif)}
											/>
										);
									})}
								</NotificationRowList>
							)}
							<Stack mb="sm" />
							<Button
								type="button"
								variant="outline"
								fullWidth
								onClick={() => openNewNotif('outbound')}
							>
								✉️ Invia notifica manuale a Socio
							</Button>
						</PanelCard>
					</CardGrid>
				</>
			)}

			{/* ── New Notification Dialog ─────────────────────────────────── */}
			<Dialog open={newOpen} onOpenChange={setNewOpen}>
				<DialogContent width="md" tone="card">
					<DialogHeader>
						<DialogTitle size="sm">Nuova Comunicazione</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleCreate}>
						<Stack gap="md">
							<DetailField label="Titolo *">
								<DetailTextInput
									value={form.titolo}
									onChange={(v) => {
										setForm((f) => ({ ...f, titolo: v }));
										if (v.trim()) setTitoloError('');
									}}
									placeholder="Es. Nuovo impianto in esercizio"
								/>
								{titoloError && (
									<Text variant="caption" tone="danger">
										{titoloError}
									</Text>
								)}
							</DetailField>
							<Grid columns={2} gap="md">
								<DetailField label="Tipo">
									<DetailSelectInput
										value={form.tipo}
										onChange={(v) => setForm((f) => ({ ...f, tipo: v as DBNotification['tipo'] }))}
										options={TIPO_OPTIONS}
										placeholder="Manuale"
									/>
								</DetailField>
								<DetailField label="Direzione">
									<DetailSelectInput
										value={form.direction}
										onChange={(v) => {
											setForm((f) => ({ ...f, direction: v as DBNotification['direction'] }));
											setDestinatarioError('');
										}}
										options={[
											{ value: 'outbound', label: 'In uscita (admin → socio)' },
											{ value: 'inbound', label: 'In entrata (socio → admin)' },
										]}
										placeholder="In uscita (admin → socio)"
									/>
								</DetailField>
							</Grid>
							<DetailField
								label={
									form.direction === 'outbound' ? 'Destinatario *' : 'Socio mittente (opzionale)'
								}
								error={destinatarioError || undefined}
							>
								<DetailSelectInput
									value={form.memberId}
									onChange={(v) => {
										setForm((f) => ({ ...f, memberId: v }));
										if (v) setDestinatarioError('');
									}}
									options={
										form.direction === 'outbound'
											? destinatarioOptions
											: members.map((m) => ({ value: m.id, label: m.nome }))
									}
									placeholder={
										form.direction === 'outbound'
											? '- scegli il destinatario -'
											: 'Mittente non specificato'
									}
								/>
							</DetailField>
							<DetailField label="Canale">
								<DetailSelectInput
									value={form.canale}
									onChange={(v) => setForm((f) => ({ ...f, canale: v }))}
									options={[
										{ value: 'portale', label: 'Portale (solo in-app)' },
										{ value: 'email', label: 'Email (invia via Resend)' },
										{ value: 'email+portale', label: 'Email + Portale' },
									]}
									placeholder={null}
								/>
								{form.canale.includes('email') && (
									<Text variant="caption" tone="accent" withIcon>
										<Mail />
										{form.memberId && form.memberId !== ALL_MEMBERS
											? 'Email inviata al socio selezionato'
											: 'Email inviata a tutti i soci con email registrata'}
									</Text>
								)}
							</DetailField>
							<DetailField label="Messaggio">
								<DetailTextarea
									value={form.messaggio}
									onChange={(v) => setForm((f) => ({ ...f, messaggio: v }))}
									rows={3}
									placeholder="Testo della comunicazione..."
								/>
							</DetailField>
							<DetailField label="Allegati (PDF, Immagini)">
								<Stack gap="sm">
									{form.allegati.length > 0 && (
										<Row wrap gap="sm">
											{form.allegati.map((a, idx) => (
												<AttachmentPill
													key={a.path}
													name={a.nome}
													onRemove={() =>
														setForm((f) => ({
															...f,
															allegati: f.allegati.filter((_, i) => i !== idx),
														}))
													}
												/>
											))}
										</Row>
									)}
									<UploadAttachButton
										uploading={uploadingAttachment}
										onFiles={handleAttachmentUpload}
										accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
										idleLabel={form.allegati.length > 0 ? 'Aggiungi altri file' : 'Allega file'}
									/>
								</Stack>
							</DetailField>
							<DialogFooter>
								<Button type="button" variant="outline" onClick={() => setNewOpen(false)}>
									Annulla
								</Button>
								<Button type="submit" variant="accentSoft" disabled={saving}>
									{saving && <Spinner size="sm" tone="inherit" />}
									Invia Comunicazione
								</Button>
							</DialogFooter>
						</Stack>
					</form>
				</DialogContent>
			</Dialog>

			{/* ── Notification Detail Dialog ──────────────────────────────── */}
			{detailNotif &&
				(() => {
					const cfg = TIPO_CONFIG[detailNotif.tipo] ?? TIPO_CONFIG.system;
					const memberName = detailNotif.memberId
						? (members.find((m) => m.id === detailNotif.memberId)?.nome ??
							`#${detailNotif.memberId.slice(0, 8)}`)
						: null;
					const tipoLabel = TIPO_LABEL[detailNotif.tipo] ?? detailNotif.tipo;
					const data = detailNotif.createdAt
						? new Date(detailNotif.createdAt).toLocaleString('it-IT', {
								day: '2-digit',
								month: 'short',
								year: 'numeric',
								hour: '2-digit',
								minute: '2-digit',
							})
						: '-';

					return (
						<Dialog open onOpenChange={(open) => !open && setDetailNotif(null)}>
							<DialogContent width="md" tone="card">
								<DialogHeader>
									<DialogTitle size="sm" withIcon>
										<span>{cfg.emoji}</span>
										{detailNotif.titolo}
									</DialogTitle>
								</DialogHeader>
								<Stack gap="md">
									<Grid columns={2} gap="md">
										<div>
											<Text variant="label" tone="muted">
												Tipo
											</Text>
											<Text>{tipoLabel}</Text>
										</div>
										<div>
											<Text variant="label" tone="muted">
												Direzione
											</Text>
											<Text>
												{detailNotif.direction === 'outbound'
													? 'In uscita (→ socio)'
													: 'In entrata (← socio)'}
											</Text>
										</div>
										{memberName && (
											<div>
												<Text variant="label" tone="muted">
													Destinatario
												</Text>
												<Text>{memberName}</Text>
											</div>
										)}
										{detailNotif.canale && (
											<div>
												<Text variant="label" tone="muted">
													Canale
												</Text>
												<Text>{detailNotif.canale}</Text>
											</div>
										)}
										<div>
											<Text variant="label" tone="muted">
												Data
											</Text>
											<Text>{data}</Text>
										</div>
										<div>
											<Text variant="label" tone="muted">
												Stato
											</Text>
											<Text
												tone={detailNotif.letto ? 'muted' : 'accent'}
												weight={detailNotif.letto ? undefined : 'semibold'}
											>
												{detailNotif.letto ? 'Letta' : 'Non letta'}
											</Text>
										</div>
									</Grid>
									{detailNotif.messaggio && (
										<div>
											<Text variant="label" tone="muted">
												Messaggio
											</Text>
											<QuoteBlock>{detailNotif.messaggio}</QuoteBlock>
										</div>
									)}
									{detailNotif.allegatoPath &&
										(() => {
											const allegati = parseAllegati(
												detailNotif.allegatoPath,
												detailNotif.allegatoNome,
											);
											return (
												<div>
													<Text variant="label" tone="muted">
														Allegati ({allegati.length})
													</Text>
													<Stack gap="xxs">
														{allegati.map((a) => (
															<AttachmentPill
																key={a.path}
																name={a.nome}
																onOpen={() => setViewAttachUrl({ url: a.path, nome: a.nome })}
															/>
														))}
													</Stack>
												</div>
											);
										})()}
								</Stack>
								<DialogFooter>
									<Button type="button" variant="outline" onClick={() => setDetailNotif(null)}>
										Chiudi
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					);
				})()}

			{/* ── Delete confirmation ─────────────────────────────────────── */}
			<ConfirmationDialog
				open={pendingDelete !== null}
				onOpenChange={(open) => !open && setPendingDelete(null)}
				title="Elimina notifica"
				description={
					pendingDelete
						? `"${pendingDelete.titolo}" verrà eliminata definitivamente. L'operazione non è reversibile.`
						: ''
				}
				confirmText="Elimina"
				isLoading={deleting}
				onConfirm={handleConfirmDelete}
			/>

			{/* Attachment inline viewer */}
			{viewAttachUrl && (
				<AttachmentViewerDialog
					url={viewAttachUrl.url}
					name={viewAttachUrl.nome}
					onClose={() => setViewAttachUrl(null)}
				/>
			)}
		</div>
	);
}

export default function NotificationsPage() {
	return (
		<Suspense>
			<NotificationsContent />
		</Suspense>
	);
}
